import React, { useEffect, useRef } from 'react'
import { useThree } from '@react-three/fiber'
import { RapierRigidBody } from '@react-three/rapier'
import { useGame } from '../contexts/GameContext'

interface SwipeHandlerProps {
  ballRef: React.RefObject<RapierRigidBody>
}

export const SwipeHandler: React.FC<SwipeHandlerProps> = ({ ballRef }) => {
  const { gl } = useThree()
  const { 
    gameState, 
    setSwipeStart, 
    setSwipeEnd, 
    calculateSwipeForce,
    startGame 
  } = useGame()

  const isSwipingRef = useRef(false)

  useEffect(() => {
    const canvas = gl.domElement

    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault()
      if (!gameState.gameStarted) {
        startGame()
      }
      
      const touch = e.touches[0]
      const rect = canvas.getBoundingClientRect()
      const x = touch.clientX - rect.left
      const y = touch.clientY - rect.top
      
      setSwipeStart({ x, y })
      setSwipeEnd(null)
      isSwipingRef.current = true
    }

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault()
      if (!isSwipingRef.current) return
      
      const touch = e.touches[0]
      const rect = canvas.getBoundingClientRect()
      const x = touch.clientX - rect.left
      const y = touch.clientY - rect.top
      
      setSwipeEnd({ x, y })
    }

    const handleTouchEnd = (e: TouchEvent) => {
      e.preventDefault()
      if (!isSwipingRef.current) return
      
      isSwipingRef.current = false
      
      const swipeData = calculateSwipeForce()
      if (swipeData && ballRef.current) {
        const { force, angle } = swipeData
        
        // Convert swipe to forward motion (towards goal)
        const forceX = -Math.cos(angle) * force * 0.3 // Horizontal movement (left/right, no inversion)
        const forceY = Math.abs(Math.sin(angle)) * force * 0.8 // Upward force
        const forceZ = force * 2 // Forward towards goal (positive Z)
        
        // Apply impulse to ball
        ballRef.current.applyImpulse({ x: forceX, y: forceY, z: forceZ }, true)
        ballRef.current.setLinvel({ x: forceX, y: forceY, z: forceZ }, true)
      }
      
      // Reset swipe state
      setSwipeStart(null)
      setSwipeEnd(null)
    }

    // Also add mouse support for desktop
    const handleMouseDown = (e: MouseEvent) => {
      e.preventDefault();
      if (!gameState.gameStarted) {
        startGame()
      }
      
      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      
      setSwipeStart({ x, y })
      setSwipeEnd(null)
      isSwipingRef.current = true
    }

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      if (!isSwipingRef.current) return
      
      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      
      setSwipeEnd({ x, y })
    }

    const handleMouseUp = (e: MouseEvent) => {
      e.preventDefault();
      if (!isSwipingRef.current) return
      
      isSwipingRef.current = false
      
      const swipeData = calculateSwipeForce()
      if (swipeData && ballRef.current) {
        const { force, angle } = swipeData
        
        const forceX = -Math.cos(angle) * force * 0.3
        const forceY = Math.abs(Math.sin(angle)) * force * 0.8
        const forceZ = force * 2 // Forward towards goal
        
        ballRef.current.applyImpulse({ x: forceX, y: forceY, z: forceZ }, true)
        ballRef.current.setLinvel({ x: forceX, y: forceY, z: forceZ }, true)
      }
      
      setSwipeStart(null)
      setSwipeEnd(null)
    }

    // Register event handlers
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false })
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false })
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false })
    
    canvas.addEventListener('mousedown', handleMouseDown)
    canvas.addEventListener('mousemove', handleMouseMove)
    canvas.addEventListener('mouseup', handleMouseUp)

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart)
      canvas.removeEventListener('touchmove', handleTouchMove)
      canvas.removeEventListener('touchend', handleTouchEnd)
      
      canvas.removeEventListener('mousedown', handleMouseDown)
      canvas.removeEventListener('mousemove', handleMouseMove)
      canvas.removeEventListener('mouseup', handleMouseUp)
    }
  }, [gl, gameState.gameStarted, setSwipeStart, setSwipeEnd, calculateSwipeForce, startGame, ballRef])

  return null // This component doesn't render any visual elements
} 